import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import usePostApprovalPlaidContext from './usePostApprovalPlaidContext';
import { getRoute } from '../actions/sessions';
import {
  processingOutcomeEventProperties,
  processingPresentation,
  processingViewEventProperties,
  ROUTED_TERMINAL_STATUSES,
  shouldRouteOnTerminal,
  terminalContinuation,
} from '../lib/utils/post_approval_plaid_processing';
import {
  capturePostApprovalPlaidEvent,
  POST_APPROVAL_PLAID_EVENTS,
} from '../lib/utils/post_approval_plaid_events';

const usePostApprovalPlaidProcessing = ({
  routeOnTerminal = ROUTED_TERMINAL_STATUSES,
  onTerminal,
} = {}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const session = useSelector((state) => state.sessions.get('session'));
  const publicKey = useSelector((state) => state.sessions.get('public_key'));
  const plaid = usePostApprovalPlaidContext();
  const [terminalState, setTerminalState] = useState(null);
  const [routeState, setRouteState] = useState(null);
  const {
    attempt: currentAttempt,
    phase: currentPhase,
    error,
    entityRefreshError,
    loading,
    pollingExhausted,
    pollCount,
  } = plaid;
  const contextKey = `${session.get(
    'application_id'
  )}|${publicKey}|${session.get('id')}`;
  const staleTerminal =
    terminalState && terminalState.contextKey !== contextKey;
  const terminalAttempt = staleTerminal ? null : terminalState?.attempt;
  const attempt = terminalAttempt || currentAttempt;
  const phase = staleTerminal
    ? 'idle'
    : terminalAttempt
    ? 'terminal'
    : currentPhase;
  const attemptId = attempt.get('attempt_id');
  const status = attempt.get('status');
  const viewStartedAt = useRef(performance.now());
  const viewedAttemptIds = useRef(new Set());
  const outcomeAttemptIds = useRef(new Set());
  const handledAttemptIds = useRef(new Set());
  const routedAttemptIds = useRef(new Set());
  const mounted = useRef(false);
  const routeKey = `${contextKey}|${attemptId}`;
  const routeError =
    routeState?.key === routeKey && routeState.status === 'error';
  const routeLoading =
    routeState?.key === routeKey && routeState.status === 'loading';
  const presentation = routeError
    ? processingPresentation({ error: routeState.error })
    : processingPresentation({
        phase,
        status,
        error: phase === 'entity_refresh_error' ? entityRefreshError : error,
        pollingExhausted,
      });
  const continuation = useMemo(() => terminalContinuation(attempt), [attempt]);
  const routeNow = shouldRouteOnTerminal(attempt, routeOnTerminal);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    viewStartedAt.current = performance.now();
    viewedAttemptIds.current = new Set();
    outcomeAttemptIds.current = new Set();
    handledAttemptIds.current = new Set();
    routedAttemptIds.current = new Set();
    setRouteState(null);
    setTerminalState(null);
  }, [contextKey]);

  useEffect(() => {
    if (currentPhase === 'terminal' && !terminalState) {
      setTerminalState({ attempt: currentAttempt, contextKey });
    }
  }, [contextKey, currentAttempt, currentPhase, terminalState]);

  useEffect(() => {
    if (
      status !== 'processing' ||
      !attemptId ||
      viewedAttemptIds.current.has(attemptId)
    ) {
      return;
    }

    viewedAttemptIds.current.add(attemptId);
    capturePostApprovalPlaidEvent(POST_APPROVAL_PLAID_EVENTS.PROCESSING_VIEWED, {
      session,
      attempt,
      ...processingViewEventProperties({ session, attempt }),
    });
  }, [attempt, attemptId, session, status]);

  useEffect(() => {
    if (phase !== 'terminal' || !attemptId) return;

    if (!outcomeAttemptIds.current.has(attemptId)) {
      outcomeAttemptIds.current.add(attemptId);
      capturePostApprovalPlaidEvent(
        POST_APPROVAL_PLAID_EVENTS.PROCESSING_OUTCOME_VIEWED,
        {
          session,
          attempt,
          ...processingOutcomeEventProperties({
            session,
            attempt,
            waitMs: viewedAttemptIds.current.has(attemptId)
              ? performance.now() - viewStartedAt.current
              : 0,
            pollCount,
            exhaustedRetries: pollingExhausted,
          }),
        }
      );
    }

    if (!handledAttemptIds.current.has(attemptId)) {
      handledAttemptIds.current.add(attemptId);
      onTerminal?.(attempt, continuation);
    }
  }, [
    attempt,
    attemptId,
    continuation,
    onTerminal,
    phase,
    pollCount,
    pollingExhausted,
    session,
  ]);

  const continueToNextRoute = useCallback(
    (history) => {
      if (
        phase !== 'terminal' ||
        !attemptId ||
        routedAttemptIds.current.has(attemptId)
      ) {
        return undefined;
      }

      routedAttemptIds.current.add(attemptId);
      if (continuation.kind === 'path') {
        history.push(continuation.path);
      } else {
        setRouteState({ key: routeKey, status: 'loading' });
        const matchesSession = () => {
          const sessions = store.getState().sessions;
          return (
            mounted.current &&
            sessions.get('public_key') === publicKey &&
            sessions.getIn(['session', 'id']) === session.get('id') &&
            sessions.getIn(['session', 'application_id']) ===
              session.get('application_id')
          );
        };
        Promise.resolve()
          .then(
            () =>
              matchesSession() &&
              dispatch(getRoute({ canApplyResponse: matchesSession }))
          )
          .then(
            () => {
              if (matchesSession()) {
                setRouteState({ key: routeKey, status: 'success' });
              }
            },
            (routeFailure) => {
              if (matchesSession()) {
                setRouteState({
                  key: routeKey,
                  status: 'error',
                  error: routeFailure,
                });
              }
            }
          );
      }

      return undefined;
    },
    [
      attemptId,
      continuation,
      dispatch,
      phase,
      publicKey,
      routeKey,
      session,
      store,
    ]
  );

  const retryRoute = useCallback(() => {
    if (!routeError || phase !== 'terminal' || continuation.kind !== 'route') {
      return;
    }
    routedAttemptIds.current.delete(attemptId);
    continueToNextRoute();
  }, [attemptId, continuation, continueToNextRoute, phase, routeError]);

  const reauthenticate = useCallback((history) => {
    history.push('/init/existing_user');
  }, []);

  return {
    attempt,
    phase,
    presentation,
    error,
    loading,
    routeLoading,
    routeError,
    pollingExhausted,
    continuation,
    routeNow,
    continueToNextRoute,
    retryRoute,
    retryStatus: plaid.retryStatus,
    retryEntities: plaid.refreshEntities,
    reauthenticate,
    deadlineAt: attempt.get('deadline_at'),
    submittedAt: attempt.get('submitted_at'),
  };
};

export default usePostApprovalPlaidProcessing;
